import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Iskelet, Kart, Secim, denemeTurAdi, netMetni } from "@koclukpanel/ui";
import {
  HEDEF_NET_DERS_EN_COK,
  HEDEF_NET_EN_COK,
  HEDEF_NET_SINAV_TURLERI,
  type HedefNetKumesi,
  type HedefNetSinavTuru,
} from "@koclukpanel/contracts";
import { ApiHatasi, dersHavuzuGetir, hedefNetGetir, hedefNetKaydet } from "../api";

/** Sunucunun sürüm uyuşmazlığı kodu (`packages/contracts/src/hedef-net.ts`). */
const SURUM_CAKISMASI = "HEDEF_NET_DEGISTI";

/**
 * DERS BAZLI HEDEF NET — F-EXM-09 (Emre kararı 07.10.2026)
 *
 * ================================================================
 * KARAR
 * ================================================================
 * Hedef neti KOÇ girer; öğrenci ve veli görür ama değiştiremez. Düzey SINAV TÜRÜ + DERS'tir. Toplam
 * hedef kullanıcıdan alınmaz: derslerin toplamıdır ve KAYITTA sunucu hesaplar. Formdaki canlı toplam
 * YALNIZ gösterimdir (koç yazarken görsün); kayıt gövdesine girmez.
 *
 * ================================================================
 * YETKİ: PROP, ROL ADI DEĞİL
 * ================================================================
 * `duzenleyebilir` `deneme.hedef.duzenle` izninden gelir (üst bileşen `izinVar` ile sorar). `false` iken
 * form ve düğme HİÇ çizilmez: salt okur kart. Asıl koruma sunucuda (koç ASSIGNED / süper yönetici); bu
 * ekran yalnız yetkisiz kişiye çalışmayacak bir düğme göstermemek için.
 *
 * Ders listesi, görev formunun kullandığı öğrenci ders havuzundan (`ders-havuzu`, öğrencinin alanından
 * sunucuda kurulur) gelir; ikinci bir ders listesi uydurulmadı. Küme tek işlemde TAM gönderilir
 * (gönderilmeyen ders kümeden çıkar); boş küme o sınav türünün hedefini temizler ve onay ister.
 *
 * ================================================================
 * SÜRÜM KOŞULU: İKİ KOÇ AYNI ANDA YAZARSA SONRAKİ SESSİZCE EZMEZ
 * ================================================================
 * Form açıldığı andaki kümeler bir kez DONDURULUR; `beklenenSurum` o anki sınav türü kümesinin `surum`'udur
 * (kümesi yoksa `null`). Arka plan yenilemesi formu ve gönderilen sürümü değiştirmez: koç hangi hâli GÖRDÜYSE
 * onun sürümünü bildirir. Sunucu 409 `HEDEF_NET_DEGISTI` derse düzenleme formda KALIR ve uyarı çıkar;
 * "Güncel hâli yükle" sorguyu yeniler ve formu güncel değerlerle yeniden açar.
 */

/** Bir form satırı: ders seçimi ve net metni (virgül ya da nokta). */
interface Satir {
  anahtar: number;
  lessonId: string;
  net: string;
}

/** "27,5" ve "27.5" kabul; boş, harf, eksi ve sınır dışı `null`. */
export function netAyristir(metin: string): number | null {
  const k = metin.trim().replace(",", ".");
  if (k.length === 0 || !/^\d+(\.\d+)?$/.test(k)) return null;
  const n = Number(k);
  return Number.isFinite(n) && n >= 0 && n <= HEDEF_NET_EN_COK ? n : null;
}

function netMetniAlana(n: number): string {
  return String(n).replace(".", ",");
}

function toplamMetni(n: number): string {
  return netMetni(Math.round(n * 100) / 100);
}

export function HedefNetKarti({
  ogrenciId,
  duzenleyebilir,
}: {
  ogrenciId: string;
  duzenleyebilir: boolean;
}) {
  const [duzenle, setDuzenle] = useState(false);
  // "Güncel hâli yükle" formu yeniden kurar: bu sayaç formun `key`'i.
  const [formSayaci, setFormSayaci] = useState(0);
  const hedefler = useQuery({
    queryKey: ["hedef-net", ogrenciId],
    queryFn: () => hedefNetGetir(ogrenciId),
    retry: false,
  });

  return (
    <Kart
      baslik="Hedef netler"
      aciklama="Sınav türü ve ders bazlı; toplam derslerin toplamıdır"
      eylem={
        duzenleyebilir && !duzenle && hedefler.data !== undefined ? (
          <Buton gorunum="ikincil" onClick={() => setDuzenle(true)}>
            Hedef netleri düzenle
          </Buton>
        ) : undefined
      }
    >
      {hedefler.isPending ? (
        <Iskelet satir={2} />
      ) : hedefler.data === undefined ? (
        // Veri varken arka plan yenilemesi başarısız olursa form (ve yazılanlar) kaybolmaz.
        <p className="text-hata-700 text-sm">Hedef netler alınamadı.</p>
      ) : duzenleyebilir && duzenle ? (
        <HedefNetFormu
          key={formSayaci}
          ogrenciId={ogrenciId}
          kumeler={hedefler.data.kumeler}
          onKapat={() => setDuzenle(false)}
          onGuncelle={async () => {
            const sonuc = await hedefler.refetch();
            if (sonuc.isError || sonuc.data === undefined) return false;
            setFormSayaci((n) => n + 1);
            return true;
          }}
        />
      ) : (
        <HedefNetOzeti kumeler={hedefler.data.kumeler} />
      )}
    </Kart>
  );
}

function HedefNetOzeti({ kumeler }: { kumeler: readonly HedefNetKumesi[] }) {
  if (kumeler.length === 0) {
    return <p className="text-notr-600 text-sm">Hedef net girilmemiş.</p>;
  }
  return (
    <div className="flex flex-col gap-4">
      {kumeler.map((k) => (
        <section key={k.examKind} aria-label={`${denemeTurAdi(k.examKind)} hedef netleri`}>
          <h3 className="text-notr-900 mb-1 text-sm font-semibold">{denemeTurAdi(k.examKind)}</h3>
          <table className="w-full text-left text-sm">
            <caption className="sr-only">
              {denemeTurAdi(k.examKind)} ders bazlı hedef netler
            </caption>
            <thead>
              <tr className="text-notr-600 border-notr-200 border-b text-xs">
                <th scope="col" className="py-1.5 pr-2 font-medium">
                  Ders
                </th>
                <th scope="col" className="py-1.5 pl-2 text-right font-medium">
                  Hedef net
                </th>
              </tr>
            </thead>
            <tbody>
              {k.dersler.map((d) => (
                <tr key={d.lessonId} className="border-notr-100 border-b">
                  <th scope="row" className="text-notr-900 py-1.5 pr-2 font-normal">
                    {d.ders}
                  </th>
                  <td className="text-notr-900 py-1.5 pl-2 text-right tabular-nums">
                    {netMetni(d.hedefNet)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" className="text-notr-900 py-1.5 pr-2 font-semibold">
                  Toplam
                </th>
                <td className="text-notr-900 py-1.5 pl-2 text-right font-semibold tabular-nums">
                  {netMetni(k.toplam)}
                </td>
              </tr>
            </tfoot>
          </table>
        </section>
      ))}
    </div>
  );
}

function satirlar(kume: HedefNetKumesi | undefined, sayac: { n: number }): Satir[] {
  return (kume?.dersler ?? []).map((d) => ({
    anahtar: sayac.n++,
    lessonId: d.lessonId,
    net: netMetniAlana(d.hedefNet),
  }));
}

function HedefNetFormu({
  ogrenciId,
  kumeler: guncelKumeler,
  onKapat,
  onGuncelle,
}: {
  ogrenciId: string;
  kumeler: readonly HedefNetKumesi[];
  onKapat: () => void;
  /** Güncel hâli sunucudan alıp formu yeniden kurar; alınamazsa `false`. */
  onGuncelle: () => Promise<boolean>;
}) {
  const qc = useQueryClient();
  /*
    F-EXM-09 — formun GÖRDÜĞÜ hâl: açıldığı andaki kümeler. Sorgu arka planda yenilense bile bu değişmez;
    `beklenenSurum` buradan okunur. Yoksa koç eski hâli görüp yeni sürümü bildirir ve başkasının yazdığını
    sessizce ezerdi.
  */
  const [kumeler] = useState(guncelKumeler);
  const sayac = useMemo(() => ({ n: 0 }), []);
  const [tur, setTur] = useState<HedefNetSinavTuru>(kumeler[0]?.examKind ?? "TYT");
  const [rows, setRows] = useState<Satir[]>(() =>
    satirlar(
      kumeler.find((k) => k.examKind === (kumeler[0]?.examKind ?? "TYT")),
      sayac,
    ),
  );
  const [temizlemeOnayi, setTemizlemeOnayi] = useState(false);
  const [denendi, setDenendi] = useState(false);
  const [guncelleniyor, setGuncelleniyor] = useState(false);
  const [guncelHatasi, setGuncelHatasi] = useState(false);

  const havuz = useQuery({
    queryKey: ["ders-havuzu", ogrenciId],
    queryFn: () => dersHavuzuGetir(ogrenciId),
    retry: false,
  });
  const gruplar = (havuz.data?.gruplar ?? []).map((g) => ({
    ad: g.alan.ad,
    dersler: g.dersler.filter((d) => d.tur === "ders"),
  }));
  // Kayıtlı bir ders havuzda yoksa (alan değişmiş olabilir) yine seçilebilir kalsın: sessizce boşalmasın.
  const mevcut = kumeler.find((k) => k.examKind === tur);
  const havuzdaki = new Set(gruplar.flatMap((g) => g.dersler.map((d) => d.id)));
  const havuzDisi = (mevcut?.dersler ?? []).filter((d) => !havuzdaki.has(d.lessonId));

  const secilenler = rows.map((r) => r.lessonId).filter((l) => l !== "");
  const dolu = rows.filter((r) => r.lessonId !== "" || r.net.trim() !== "");
  // Canlı toplam YALNIZ gösterim; geçerli satırların toplamı. Kayıttaki toplamı sunucu hesaplar.
  const canliToplam = dolu.reduce((t, r) => t + (netAyristir(r.net) ?? 0), 0);

  const satirHatasi = (r: Satir): { ders?: string; net?: string } => {
    if (r.lessonId === "" && r.net.trim() === "") return {};
    return {
      ders: r.lessonId === "" ? "Bir ders seç." : undefined,
      net:
        netAyristir(r.net) === null
          ? `0 ile ${HEDEF_NET_EN_COK} arasında bir net yaz (örn. 27,5).`
          : undefined,
    };
  };
  const gecerli = dolu.every((r) => {
    const h = satirHatasi(r);
    return !h.ders && !h.net;
  });

  const mut = useMutation({
    mutationFn: () =>
      hedefNetKaydet(ogrenciId, {
        examKind: tur,
        // Formu açtığınız andaki küme sürümü; kümesi yoksa null ("bu türde hedef yoktu").
        beklenenSurum: mevcut?.surum ?? null,
        hedefler: dolu.map((r) => ({ lessonId: r.lessonId, targetNet: netAyristir(r.net)! })),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["hedef-net", ogrenciId] });
      // Trend satırlarındaki `hedefNet` de değişti: grafik ve "son deneme" kartı tazelenmeli.
      await qc.invalidateQueries({ queryKey: ["deneme-trendi", ogrenciId] });
      onKapat();
    },
  });

  const turDegistir = (yeni: HedefNetSinavTuru) => {
    setTur(yeni);
    setRows(
      satirlar(
        kumeler.find((k) => k.examKind === yeni),
        sayac,
      ),
    );
    setTemizlemeOnayi(false);
    setDenendi(false);
    mut.reset();
  };

  const kaydet = () => {
    setDenendi(true);
    if (!gecerli) return;
    if (dolu.length === 0) {
      // Boş küme = o türün hedefini temizle. Hiç kaydı olmayan türde temizlenecek bir şey yok.
      if (mevcut === undefined) return;
      setTemizlemeOnayi(true);
      return;
    }
    mut.mutate();
  };

  const bosVeKayitsiz = dolu.length === 0 && mevcut === undefined;
  // Sürüm uyuşmazlığında aynı eski sürümle tekrar göndermek yine 409 alır: önce güncel hâl yüklenmeli.
  const surumCakisti = mut.error instanceof ApiHatasi && mut.error.kod === SURUM_CAKISMASI;

  const guncelHaliYukle = async () => {
    setGuncelleniyor(true);
    setGuncelHatasi(false);
    const basari = await onGuncelle();
    // Başarıda form yeniden kurulur (bu bileşen kalkar); yalnız başarısızlıkta buradayız.
    if (!basari) {
      setGuncelHatasi(true);
      setGuncelleniyor(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        kaydet();
      }}
      noValidate
    >
      <div className="max-w-xs">
        <Secim
          etiket="Sınav türü"
          value={tur}
          onChange={(e) => turDegistir(e.target.value as HedefNetSinavTuru)}
          yardim="Her sınav türünün hedefi ayrı kaydedilir."
        >
          {HEDEF_NET_SINAV_TURLERI.map((t) => (
            <option key={t} value={t}>
              {denemeTurAdi(t)}
            </option>
          ))}
        </Secim>
      </div>

      {havuz.isError && (
        <p role="alert" className="text-hata-700 text-sm">
          Ders listesi alınamadı. Öğrencinin alanı seçilmemiş olabilir; sayfayı yenileyip tekrar
          dene.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {rows.map((r, sira) => {
          const h = denendi ? satirHatasi(r) : {};
          return (
            <div
              key={r.anahtar}
              className="grid items-start gap-2 sm:grid-cols-[minmax(0,1fr)_9rem_auto]"
            >
              <Secim
                etiket={`Ders (satır ${sira + 1})`}
                value={r.lessonId}
                hata={h.ders}
                onChange={(e) =>
                  setRows((onceki) =>
                    onceki.map((x) =>
                      x.anahtar === r.anahtar ? { ...x, lessonId: e.target.value } : x,
                    ),
                  )
                }
              >
                <option value="">Ders seç</option>
                {havuzDisi.map((d) => (
                  <option
                    key={d.lessonId}
                    value={d.lessonId}
                    disabled={secilenler.includes(d.lessonId) && d.lessonId !== r.lessonId}
                  >
                    {d.ders}
                  </option>
                ))}
                {gruplar.map((g) => (
                  <optgroup key={g.ad} label={g.ad}>
                    {g.dersler.map((d) => (
                      <option
                        key={d.id}
                        value={d.id}
                        // Aynı ders iki kez seçilemez (sunucu da reddeder): başka satırda seçiliyse kapalı.
                        disabled={secilenler.includes(d.id) && d.id !== r.lessonId}
                      >
                        {d.ad}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Secim>
              <Alan
                etiket={`Hedef net (satır ${sira + 1})`}
                inputMode="decimal"
                autoComplete="off"
                value={r.net}
                hata={h.net}
                onChange={(e) =>
                  setRows((onceki) =>
                    onceki.map((x) =>
                      x.anahtar === r.anahtar ? { ...x, net: e.target.value } : x,
                    ),
                  )
                }
              />
              <div className="sm:pt-7">
                <Buton
                  gorunum="sessiz"
                  aria-label={`Satır ${sira + 1} çıkar`}
                  onClick={() => setRows((onceki) => onceki.filter((x) => x.anahtar !== r.anahtar))}
                >
                  Çıkar
                </Buton>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="text-notr-600 text-sm">
            {mevcut === undefined
              ? `${denemeTurAdi(tur)} için hedef yok. Ders ekleyerek başla.`
              : "Tüm dersler çıkarıldı; kaydedersen bu türün hedefi temizlenir."}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Buton
          gorunum="ikincil"
          disabled={rows.length >= HEDEF_NET_DERS_EN_COK}
          onClick={() =>
            setRows((onceki) => [...onceki, { anahtar: sayac.n++, lessonId: "", net: "" }])
          }
        >
          Ders ekle
        </Buton>
        <p className="text-notr-900 text-sm" aria-live="polite">
          Toplam hedef: <strong className="tabular-nums">{toplamMetni(canliToplam)}</strong> net
          <span className="text-notr-600 text-xs"> (kayıtta sunucu hesaplar)</span>
        </p>
      </div>

      {temizlemeOnayi && (
        <div
          role="alertdialog"
          aria-label="Hedefi temizle"
          className="rounded-alan bg-uyari-50 p-3"
        >
          <p className="text-uyari-800 mb-2 text-sm">
            {denemeTurAdi(tur)} için kayıtlı bütün ders hedefleri silinecek. Emin misin?
          </p>
          <div className="flex flex-wrap gap-2">
            <Buton
              yukleniyor={mut.isPending}
              onClick={() => {
                setTemizlemeOnayi(false);
                mut.mutate();
              }}
            >
              Evet, hedefi temizle
            </Buton>
            <Buton gorunum="ikincil" onClick={() => setTemizlemeOnayi(false)}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}

      {surumCakisti && (
        <div role="alert" className="rounded-alan bg-uyari-50 flex flex-col gap-2 p-3">
          <p className="text-uyari-800 text-sm font-medium">
            Hedef netler siz açtıktan sonra değişti. Güncel hâli yükleyin; değişikliklerinizi
            yeniden uygulayın.
          </p>
          <div>
            <Buton
              gorunum="ikincil"
              yukleniyor={guncelleniyor}
              onClick={() => void guncelHaliYukle()}
            >
              Güncel hâli yükle
            </Buton>
          </div>
          {guncelHatasi && (
            <p className="text-hata-700 text-sm">Güncel hâl alınamadı. Lütfen tekrar deneyin.</p>
          )}
        </div>
      )}
      {mut.isError && !surumCakisti && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Hedef netler kaydedilemedi."}
        </p>
      )}
      {denendi && !gecerli && (
        <p role="alert" className="text-hata-700 text-sm">
          Hatalı satırları düzelt, sonra tekrar kaydet.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton type="submit" yukleniyor={mut.isPending} disabled={bosVeKayitsiz || surumCakisti}>
          Kaydet
        </Buton>
        <Buton gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </form>
  );
}
